import { Stylable } from '@core/interfaces/styles/styles.interface';
import { Block } from '@core/models/block/block.model';
import { appendToStyles, filterStyles, sortMediaQueries } from '@shared/helpers/block-helpers';
import { MediaBuilder } from './media-builder';
import { StyleBuilder } from './style-builder';
import { createStyleRecursive, getBgSizeForAnimationName } from './styles-helpers';

export class MediaQueryBuilder {
  static build(blocks: Block[]): string {
    let mergeMediaQueryStyles = '';
    const flatStyles = createStyleRecursive(blocks);

    for (const blockStyle of flatStyles) {
      let blockClassName = `.${blockStyle.uId}`;
      if (blockStyle?.modelId) {
        blockClassName = blockClassName + `.m${blockStyle.modelId}`;
      }
      let mergeMediaQuery = '';
      if (blockStyle.styles) {
        for (const query of Object.getOwnPropertyNames(blockStyle.styles)) {
          let blockClassName = `.${blockStyle.uId}`;
          if (blockStyle.modelId) {
            blockClassName += `.m${blockStyle.modelId}`;
          }
          const media = MediaBuilder.media(query);
          const groups = StyleBuilder.build(
            blockClassName,
            blockStyle.styles[query],
          );
          mergeMediaQuery = `${mergeMediaQuery} ${media} ${groups}`;
        }
      }

      mergeMediaQueryStyles += mergeMediaQuery;
    }

    return mergeMediaQueryStyles;
  }

  static buildRoot(block: Stylable, tagName = 'app-root'): string {
    const styles = sortMediaQueries(block.styles) || {};
    let mergeMediaQuery = '';
    for (const query of Object.getOwnPropertyNames(styles)) {
      const media = MediaBuilder.media(query);
      const groups = StyleBuilder.build(tagName, styles[query]);
      mergeMediaQuery = `${mergeMediaQuery} ${media} ${groups}`;
    }
    return mergeMediaQuery;
  }

  static buildRootPageStyle(block: Stylable): string {
    const tagName = 'app-root';
    const styles = sortMediaQueries(block.styles) || {};
    const bgStyle = filterStyles(styles, (prop: string) => {
      return prop.startsWith('background');
    });
    const animationStyle = filterStyles(styles, (prop: string) => {
      return prop.startsWith('animation');
    });
    // Animacja tła jest przeznaczona dla warstwy `app-root:after`. Na samym `app-root` klatki
    // bgSizeUp/bgSizeDown (`scale`) powiększałyby całą stronę razem z menu i stopką.
    const notBgStyle = filterStyles(styles, (prop: string) => {
      return !prop.startsWith('background') && !prop.startsWith('animation');
    });

    let mergeMediaQuery = '';
    for (const query of Object.getOwnPropertyNames(notBgStyle)) {
      const media = MediaBuilder.media(query);
      const groups = StyleBuilder.build(tagName, notBgStyle[query]);
      mergeMediaQuery = `${mergeMediaQuery} ${media} ${groups}`;
    }

    const fixedStyleForAnimation = this.applyNecessaryStyleForBgAnimation(animationStyle);

    const filledBgStyle = appendToStyles(bgStyle, {
      content: '\' \'',
      position: 'fixed',
      top: '0',
      'min-height': '100vh',
      height: '100%',
      left: '0',
      right: '0',
      'z-index': '-1',

      ...fixedStyleForAnimation,
    });

    for (const query of Object.getOwnPropertyNames(filledBgStyle)) {
      const media = MediaBuilder.media(query);
      const groups = StyleBuilder.build(`${tagName}:after`, filledBgStyle[query]);
      mergeMediaQuery = `${mergeMediaQuery} ${media} ${groups}`;
    }
    return mergeMediaQuery;
  }

  private static applyNecessaryStyleForBgAnimation(animationStyle: any) {
    animationStyle = animationStyle?.all?.basic || {};
    if (!Object.keys(animationStyle).length) {
      return {};
    }
    if (!animationStyle.animationName) {
      return animationStyle;
    }
    const bgSize = getBgSizeForAnimationName(animationStyle.animationName);
    if (bgSize) {
      animationStyle.backgroundSize = bgSize;
    }
    return animationStyle;
  }
}
